// Modal cấu hình HẠN MỨC RÚT TIỀN: tối thiểu và tối đa mỗi lệnh rút.
// Vượt trần -> API trả 400 "Giao dịch của bạn quá lớn. Vui lòng rút tối đa X/lần."
// (app + web cũng đọc hạn mức này để báo trước khi user tốn một lần OTP).
import { useEffect, useState } from "react";
import { Modal, InputNumber, message, Alert } from "antd";
import paymentApi from "../../../api/paymentApi";

interface Props {
    open: boolean;
    onClose: () => void;
}

const DEFAULT_RANGE = { floor: 1000, ceil: 500000000 };

export default function WithdrawLimitModal({ open, onClose }: Props) {
    const [minAmount, setMinAmount] = useState<number | null>(null);
    const [maxAmount, setMaxAmount] = useState<number | null>(null);
    const [range, setRange] = useState(DEFAULT_RANGE);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Mở modal -> tải giá trị hiện tại (đọc thẳng DB, không qua cache 30s)
    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                setLoading(true);
                const { data } = await paymentApi.getWithdrawLimits();
                const cfg = data?.data || {};
                setMinAmount(cfg.minAmount ?? null);
                setMaxAmount(cfg.maxAmount ?? null);
                setRange({ floor: cfg.floor ?? DEFAULT_RANGE.floor, ceil: cfg.ceil ?? DEFAULT_RANGE.ceil });
            } catch {
                message.error("Không thể tải hạn mức rút tiền");
            } finally {
                setLoading(false);
            }
        })();
    }, [open]);

    const handleSave = async () => {
        const inRange = (v: number | null) =>
            Number.isInteger(v) && (v as number) >= range.floor && (v as number) <= range.ceil;
        if (!inRange(minAmount) || !inRange(maxAmount)) {
            message.error(
                `Vui lòng nhập số từ ${range.floor.toLocaleString("vi-VN")}đ đến ${range.ceil.toLocaleString("vi-VN")}đ`
            );
            return;
        }
        if ((maxAmount as number) < (minAmount as number)) {
            message.error("Mức tối đa phải lớn hơn hoặc bằng mức tối thiểu");
            return;
        }
        try {
            setSaving(true);
            const { data } = await paymentApi.updateWithdrawLimits({
                minAmount: minAmount as number,
                maxAmount: maxAmount as number,
            });
            const saved = data?.data || {};
            message.success(
                `Đã lưu: rút từ ${Number(saved.minAmount).toLocaleString("vi-VN")}đ đến ${Number(
                    saved.maxAmount
                ).toLocaleString("vi-VN")}đ/lần`
            );
            onClose();
        } catch {
            message.error("Không thể lưu hạn mức");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            title="Hạn mức rút tiền"
            open={open}
            onOk={handleSave}
            onCancel={onClose}
            okText="Lưu"
            cancelText="Đóng"
            confirmLoading={saving}
        >
            <Alert
                type="info"
                showIcon
                className="mb-3"
                message="Áp dụng cho MỖI lệnh rút. Lệnh vượt mức tối đa sẽ bị từ chối kèm thông báo gợi ý user tách nhỏ lệnh. Có hiệu lực trong vòng 30 giây sau khi lưu."
            />
            <div className="flex flex-col gap-3">
                <label className="flex flex-col gap-1">
                    <span className="text-sm text-content-2">Số tiền rút tối thiểu (đ)</span>
                    <InputNumber
                        className="w-full"
                        min={range.floor}
                        max={range.ceil}
                        step={1000}
                        value={minAmount}
                        onChange={(v) => setMinAmount(v as number | null)}
                        disabled={loading}
                        formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                        parser={(v) => Number((v || "").replace(/\D/g, ""))}
                    />
                </label>
                <label className="flex flex-col gap-1">
                    <span className="text-sm text-content-2">Số tiền rút tối đa mỗi lần (đ)</span>
                    <InputNumber
                        className="w-full"
                        min={range.floor}
                        max={range.ceil}
                        step={1000}
                        value={maxAmount}
                        onChange={(v) => setMaxAmount(v as number | null)}
                        disabled={loading}
                        formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                        parser={(v) => Number((v || "").replace(/\D/g, ""))}
                    />
                </label>
                {maxAmount ? (
                    <span className="text-xs text-content-3">
                        User rút quá mức này sẽ thấy: “Giao dịch của bạn quá lớn. Vui lòng rút tối đa{" "}
                        {Number(maxAmount).toLocaleString("vi-VN")}đ/lần.”
                    </span>
                ) : null}
            </div>
        </Modal>
    );
}
